/**
 * Subscribes to the signed-in user's ID-token claims and returns
 * whether the `tester: true` custom claim is present.
 *
 * Provisioned out-of-band: an admin sets the claim via the Firebase
 * Admin SDK (`auth().setCustomUserClaims(uid, { tester: true })`).
 * Paying customers never have this claim, so the gated UI never
 * renders for them.
 *
 * The hook starts pessimistic (`false`) and flips to `true` once the
 * token result resolves with the claim. That ordering guarantees a
 * paying customer cannot briefly see the gated UI during the auth
 * round-trip even if the network is slow — they get `false`
 * throughout, the gated render happens (or doesn't) only after the
 * token resolves.
 *
 * The hook does NOT force a token refresh — Firebase already
 * refreshes the token roughly hourly, and `FirebaseApp.tsx` calls
 * `getIdToken(true)` once on first hydrate to flush email-verified
 * stale tokens. The claim becomes visible on the next natural
 * refresh after provisioning, which is fine for an internal tooling
 * gate.
 */
import { useEffect, useState } from 'react';
import type { User } from 'firebase/auth';

export function useTesterClaim(user: User | null): boolean {
  const [isTester, setIsTester] = useState(false);

  useEffect(() => {
    if (user === null) {
      setIsTester(false);
      return;
    }
    let cancelled = false;
    user
      .getIdTokenResult()
      .then((result) => {
        if (cancelled) return;
        setIsTester(result.claims['tester'] === true);
      })
      .catch(() => {
        if (cancelled) return;
        setIsTester(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return isTester;
}
